import { useGlobalState } from './useGlobalState'

type Authorizations =
  | 'LoggedIn'
  | 'UserIsAdmin'
  | 'UserIsSupervisor'
  | 'UserIsMember'
  | 'FlagAllowImport'
  | 'FlagShowArchived'

type BaseAuthRequest = {
  [K in Authorizations]?: boolean
}

type AuthRequest = BaseAuthRequest | { and: BaseAuthRequest } | { or: BaseAuthRequest }

export function useAuthorization(requests: AuthRequest): boolean {
  const { authenticationStore, configStore } = useGlobalState()
  const { isAuthenticated, user } = authenticationStore
  const { flags } = configStore
  const { role } = user ?? {}

  function resolveAuth(request: AuthRequest): boolean {
    if ('and' in request) {
      return resolveCurrentLevel(request.and, 'and')
    }

    if ('or' in request) {
      return resolveCurrentLevel(request.or, 'or')
    }

    return resolveCurrentLevel(request, 'and')
  }

  // Helper to resolve current level authorization requests
  function resolveCurrentLevel(request: BaseAuthRequest, op: 'and' | 'or' = 'and'): boolean {
    const results: boolean[] = []

    // Process each authorization request at current level
    Object.entries(request).forEach(([key, value]) => {
      let result: boolean = false
      const auth = key as Authorizations

      switch (auth) {
        case 'LoggedIn':
          result = isAuthenticated
          break
        case 'UserIsAdmin':
          result = role === 'admin'
          break
        case 'UserIsSupervisor':
          result = role === 'supervisor'
          break
        case 'UserIsMember':
          result = role === 'member'
          break
        case 'FlagAllowImport':
          result = !!flags?.allowImport
          break
        case 'FlagShowArchived':
          result = !!flags?.showArhived
          break
      }

      // Compare the actual state with the requested value
      results.push(result === value)
    })

    // If no explicit authorizations at this level, return true
    // This allows for pure AND/OR nesting without requiring a check at every level
    if (results.length === 0) {
      return true
    }

    if (op === 'or') {
      // Any authorization request at this level must pass
      return results.some(Boolean)
    } else {
      // All authorization requests at this level must pass
      return results.every(Boolean)
    }
  }

  return resolveAuth(requests)
}
